/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { token } from '@atlaskit/tokens';

export const Panel = styled.section({
  backgroundColor: token('elevation.surface'),
  borderRadius: token('radius.small'),
  textAlign: 'left',
});

// We use <h2> headings in the sidebar to create semantic structure and make it accessible.
// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Heading_Elements#accessibility_concerns
export const PanelHeadingWrapper = styled.h2({
  marginTop: 0,
  marginBottom: 0,
});

export const PanelHeading = styled.button<{
  isCollapsed: boolean;
  isExpandable: boolean;
}>(({ isCollapsed, isExpandable }) => ({
  alignItems: 'center',
  backgroundColor: token('elevation.surface'),
  border: '0 none transparent',
  borderBottom: `${token('border.width')} solid ${token('color.border')}`,

  borderRadius: isCollapsed
    ? token('radius.small')
    : `${token('radius.small')} ${token('radius.small')} 0 0`,

  boxSizing: 'border-box',
  /* needed to prevent Safari setting to "activebuttontext" (white) */
  color: 'inherit',
  cursor: isExpandable ? 'pointer' : 'inherit',
  display: 'flex',
  flexWrap: 'wrap',
  minHeight: '40px',
  padding: token('space.100'),
  position: 'sticky',
  top: 0,
  /* Stop avatars from hovering over this sticky heading */
  zIndex: 1,
  textAlign: 'left',
  font: token('font.body'),
  width: '100%',
  outline: 'none',
  // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
  lineHeight: 'inherit',

  '&:hover': {
    boxShadow:
      isExpandable && isCollapsed
        ? `0 0 0 1px ${token('color.background.information')}`
        : 'none',
  },

  '&:focus, &:active': {
    boxShadow: isExpandable
      ? `0 0 0 1px ${token('color.background.information')}`
      : 'none',
  },
}));

export const PanelHeadingLabel = styled.span({
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

// This is the default styling applied to @atlaskit/panel headings
// THIS IS A COPY FROM src/sections/global/components/page.style
export const PanelHeader = styled.h2({
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  font: token('font.heading.xsmall'),
});

type PanelBodyProps = {
  isOverflowVisible?: boolean;
  panelBodyPadding?: string;
};

export const ExpanderPanelBody = styled.div`
  border-radius: 0 0 ${token('radius.small')} ${token('radius.small')};
  flex-flow: column;
  overflow: ${({ isOverflowVisible }: PanelBodyProps) =>
    isOverflowVisible ? 'visible' : 'hidden'};
  padding: ${(props: PanelBodyProps) =>
    props.panelBodyPadding ? `${props.panelBodyPadding}` : token('space.100')};
`;

export const AnimateHeightContainer = styled.div<{
  allowMaxHeight: boolean;
}>`
  max-height: ${({ allowMaxHeight }) => (allowMaxHeight ? '800px' : 'none')};
  overflow-y: ${({ allowMaxHeight }) => (allowMaxHeight ? 'auto' : 'visible')};
`;

export const Icon = styled.span({
  display: 'flex',
  marginRight: '4px',
});

// allows for caller to pass in an AK Icon or
// an AK Tooltip wrapped one with any tag name specified
export const IconSecondary = styled.div({
  display: 'flex',
  '> *': {
    display: 'flex',
  },
});

export const Chevron = styled.span({
  display: 'flex',
  marginLeft: 'auto',
});

export const Spinner = styled.div({
  '& > div': {
    display: 'flex',
  },
});

export const WarningWrapper = styled.span({
  color: token('color.text.warning'),
  display: 'flex',
  button: {
    display: 'flex',
  },
});

export const GrayDivider = styled.hr({
  backgroundColor: token('color.background.accent.gray.subtler'),
  height: token('space.025'),
  border: 'none',
});
